{
  "blocks": [],
  "relationships": [],
  "localeVariants": [
    {
      "id": "moon-article-ivf-vs-iui",
      "locale": "fr",
      "variant": {
        "title": "FIV vs IIU : laquelle est faite pour toi ?",
        "subtitle": "Le comparatif des deux traitements de fertilité les plus courants.",
        "body": {
          "introduction": "Choix difficile",
          "quickSummary": [
            {
              "title": "L'IIU est plus simple et moins chère.",
              "description": "L'IIU est moins invasive et moins chère, mais avec des taux de réussite par cycle plus faibles (10 à 20 %)."
            },
            {
              "title": "La FIV offre un taux de réussite plus élevé.",
              "description": "La FIV est plus lourde mais offre des taux de réussite de 40 à 60 % par transfert et permet le diagnostic génétique."
            },
            {
              "title": "Beaucoup commencent par l'IIU.",
              "description": "Beaucoup de patientes commencent par l'IIU et passent à la FIV. Ton médecin t'aide à choisir selon le diagnostic, l'âge, et le temps disponible."
            }
          ],
          "sections": [
            {
              "title": "Quand l'IIU a du sens",
              "content": "L'IIU (insémination intra-utérine) est souvent le traitement de première intention pour plusieurs diagnostics courants. C'est une procédure peu invasive où du sperme lavé et concentré est placé directement dans l'utérus autour du moment de l'ovulation.",
              "bulletPoints": [
                "Facteur masculin léger (numération ou mobilité légèrement basses)",
                "Infertilité inexpliquée, en première étape",
                "Troubles de l'ovulation comme le PMOS (ex SOPK) (combiné à un traitement médicamenteux)",
                "Personnes seules ou couples de même sexe utilisant un don de sperme"
              ]
            },
            {
              "title": "Quand la FIV est recommandée",
              "content": "La FIV contourne plusieurs des obstacles qui peuvent empêcher une conception naturelle ou la réussite d'une IIU. Ton médecin peut recommander d'aller directement vers la FIV dans certaines situations où l'IIU a peu de chances de marcher.",
              "bulletPoints": [
                "Trompes de Fallope bouchées ou abîmées",
                "Facteur masculin sévère",
                "Âge maternel avancé (généralement 38 ans et plus)",
                "Réserve ovarienne diminuée (AMH ou CFA bas)",
                "Endométriose modérée à sévère",
                "Plusieurs cycles d'IIU échoués (généralement 3 à 4)",
                "Besoin de diagnostic génétique préimplantatoire (PGT)"
              ]
            },
            {
              "title": "Comparer les deux processus côte à côte",
              "content": "L'IIU est une procédure relativement rapide au cabinet : le suivi prend quelques visites, et l'insémination elle-même dure environ 10 minutes sans anesthésie. La FIV implique 10 à 14 jours d'injections quotidiennes, de fréquentes visites de suivi, une ponction ovocytaire sous sédation, la culture des embryons en laboratoire, puis une procédure de transfert. L'investissement en temps et les exigences physiques sont très différents."
            },
            {
              "title": "Couverture et coûts",
              "content": "En France, dans le parcours AMP conventionné, IIU et FIV sont prises en charge à 100 % par la Sécurité sociale jusqu'au 43e anniversaire de la femme, dans la limite de 6 inséminations et 4 tentatives de FIV. Le coût n'est donc pas un facteur de décision pour la patiente dans la grande majorité des cas. Aux États-Unis et dans les pays sans couverture publique, la différence financière est en revanche un facteur majeur : IIU autour de 500 à 2 000 $ par cycle (plus 500 à 1 500 $ de médicaments), FIV autour de 15 000 à 25 000 $ par cycle.",
              "bulletPoints": [
                "France : IIU et FIV remboursées à 100 % dans le parcours AMP (jusqu'à 43 ans, 6 IIU + 4 FIV)",
                "États-Unis (sans assurance) : IIU 500 à 2 000 $, FIV 15 000 à 25 000 $ par cycle",
                "Au-delà des cycles remboursés en France, compte 3 000 à 6 000 € par cycle de FIV hors Sécu",
                "Tarifs accessibles en France via les rapports annuels de l'Agence de la biomédecine"
              ]
            },
            {
              "title": "Taux de réussite selon l'âge et le diagnostic",
              "content": "Les taux de réussite de l'IIU vont de 10 à 20 % par cycle selon l'âge et le diagnostic. Les taux de FIV sont nettement plus élevés, à 40 à 60 % par transfert pour les patientes de moins de 35 ans, et déclinent avec l'âge. Pour les patientes de plus de 40 ans, les taux de réussite de l'IIU tombent sous les 5 % par cycle, rendant la FIV plus efficace. Ton diagnostic spécifique compte aussi : une infertilité inexpliquée peut bien répondre à l'IIU dans un premier temps, alors qu'une infertilité tubaire impose la FIV."
            },
            {
              "title": "Le facteur émotionnel dans le choix",
              "content": "La décision entre IIU et FIV n'est pas seulement médicale ou financière : elle est émotionnelle. L'IIU paraît moins intense, et beaucoup de patientes préfèrent commencer par là pour entrer doucement dans le traitement. La FIV peut sembler écrasante mais peut faire gagner du temps et de l'énergie émotionnelle à long terme si l'IIU a peu de chances de marcher. Il n'y a pas de mauvaise réponse : le meilleur choix est celui qui s'aligne avec ta réalité médicale, ta situation financière, et ta disponibilité émotionnelle."
            }
          ],
          "references": [
            {
              "title": "Intra-uterine insemination for unexplained subfertility",
              "authors": "Veltman-Verhulst SM, Hughes E, Ayeleke RO, Cohlen BJ.",
              "journal": "Cochrane Database of Systematic Reviews",
              "year": 2016,
              "url": "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD001838.pub5/full",
              "evidenceLevel": "meta-analysis"
            },
            {
              "title": "Fertility problems: assessment and treatment (CG156)",
              "authors": "NICE",
              "year": 2017,
              "url": "https://www.nice.org.uk/guidance/cg156",
              "evidenceLevel": "guideline"
            },
            {
              "title": "L'assistance médicale à la procréation (AMP)",
              "authors": "Assurance Maladie (ameli.fr)",
              "url": "https://www.ameli.fr/assure/remboursements/rembourse/assistance-medicale-la-procreation-amp/prise-en-charge-de-l-assistance-medicale-la-procreation-amp",
              "evidenceLevel": "official"
            },
            {
              "title": "Rapport médical et scientifique annuel : taux de réussite par technique en France",
              "authors": "Agence de la biomédecine",
              "url": "https://rams.agence-biomedecine.fr/",
              "evidenceLevel": "official"
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-when-to-change-clinics",
      "locale": "fr",
      "variant": {
        "title": "Quand changer de clinique",
        "subtitle": "Les signes qu'il est peut-être temps, et comment faire la transition.",
        "body": {
          "introduction": "Choix difficiles",
          "quickSummary": [
            {
              "title": "C'est plus courant que tu ne le penses.",
              "description": "Changer de clinique est plus courant que ce que les gens imaginent, et ce n'est pas un manque de loyauté."
            },
            {
              "title": "Surveille les signaux d'alarme.",
              "description": "Les signaux d'alarme incluent : mauvaise communication, protocoles standardisés, et balayage de tes préoccupations."
            },
            {
              "title": "Ton dossier t'appartient.",
              "description": "Ton dossier t'appartient. Le transfert est simple."
            }
          ],
          "sections": [
            {
              "title": "Raisons valables d'envisager un changement",
              "content": "Il y a plusieurs raisons légitimes qui poussent les patientes à changer de clinique. Les reconnaître peut aider à distinguer entre les frustrations normales d'un traitement et des vrais problèmes de prise en charge.",
              "bulletPoints": [
                "Rupture de communication : tu n'arrives pas à joindre ton équipe ou tu te sens ignorée",
                "Aucun ajustement du protocole après un cycle qui n'a pas marché",
                "L'emplacement ou les horaires de la clinique créent un stress excessif",
                "Tu veux un second avis sur ton plan de traitement",
                "La clinique n'offre pas les services dont tu as besoin (PGT, programme de don, etc.)"
              ]
            },
            {
              "title": "Signaux d'alarme vs frustrations normales",
              "content": "Le traitement de fertilité est par nature difficile, et toute frustration ne signifie pas qu'il te faut une nouvelle clinique. Un cycle qui n'a pas marché n'est pas automatiquement la faute de la clinique : même les protocoles parfaits ne garantissent pas la réussite. Cependant, une clinique qui n'ajuste jamais son approche, balaie tes questions, ou te donne l'impression d'être un numéro plutôt qu'une patiente, c'est une autre histoire. La différence clé est de savoir si la clinique est réactive, transparente, et prête à faire évoluer ton plan de soin."
            },
            {
              "title": "Comment obtenir un second avis sans tout changer",
              "content": "Tu n'as pas besoin de t'engager à partir avant d'explorer d'autres options. Beaucoup de patientes demandent un second avis tout en continuant avec leur clinique actuelle. La plupart des médecins de la reproduction y sont habitués et ne le prendront pas mal. En France, une consultation de second avis avec un·e médecin de la reproduction conventionné·e secteur 1 est remboursée par la Sécurité sociale dans le cadre du parcours AMP. En secteur 2, des dépassements d'honoraires peuvent s'appliquer."
            },
            {
              "title": "Comment se passe le transfert concrètement",
              "content": "Si tu décides de changer, la logistique est plus simple que ce que la plupart des gens pensent. Le processus est bien rodé et les cliniques gèrent régulièrement des transferts.",
              "bulletPoints": [
                "Demande ton dossier médical complet : il t'appartient légalement (article L1111-7 du Code de la santé publique)",
                "Si tu as des embryons ou ovocytes congelés en France, le transfert inter-CECOS est encadré et généralement intégré au parcours AMP",
                "Aux États-Unis ou hors parcours, le cryotransport via un prestataire spécialisé coûte 500 à 1 500 $",
                "Prévoir 2 à 4 semaines pour le transfert du dossier et l'accueil par la nouvelle clinique",
                "Ta nouvelle clinique passera tout en revue avant de recommander un plan"
              ]
            },
            {
              "title": "Que demander à une nouvelle clinique",
              "content": "Quand tu consultes une nouvelle clinique, viens préparée avec ton historique complet. Partage les protocoles utilisés, comment tu as répondu, et quels ont été les résultats. Demande à la nouvelle médecin ce qu'elle ferait différemment et pourquoi. Sois attentive à savoir si elle s'engage vraiment avec ton historique ou se contente d'appliquer son approche standard.",
              "bulletPoints": [
                "Qu'est-ce que vous changeriez à mon protocole précédent ?",
                "Comment gérez-vous la communication et le suivi ?",
                "Quelle est votre approche quand un cycle ne marche pas ?",
                "Puis-je consulter vos taux de réussite par âge et indication (rapport annuel de l'Agence de la biomédecine en France) ?"
              ]
            },
            {
              "title": "Aspects émotionnels de la décision",
              "content": "Quitter une clinique peut être étonnamment émotionnel. Tu peux ressentir de la culpabilité, de la loyauté envers ton médecin, ou la peur que recommencer à zéro te fasse perdre du temps. Ces ressentis sont normaux. Souviens-toi que défendre ses intérêts n'est pas de la déloyauté, c'est de la bonne prise en charge de soi. Ton objectif est un bébé, pas une relation avec une clinique. Si ton intuition te dit que quelque chose ne va pas, fais-lui confiance."
            }
          ],
          "references": [
            {
              "title": "Centres d'AMP autorisés et indicateurs de qualité en France",
              "authors": "Agence de la biomédecine",
              "url": "https://www.procreation-medicale.fr/les-centres-autorises/",
              "evidenceLevel": "official"
            },
            {
              "title": "Rapport médical et scientifique annuel (résultats par centre)",
              "authors": "Agence de la biomédecine",
              "url": "https://rams.agence-biomedecine.fr/",
              "evidenceLevel": "official"
            },
            {
              "title": "Code de la santé publique, article L1111-7 (accès au dossier médical)",
              "authors": "Légifrance",
              "url": "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000037986942/",
              "evidenceLevel": "official"
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-fresh-vs-frozen-choice",
      "locale": "fr",
      "variant": {
        "title": "Transfert frais vs congelé : faire le bon choix",
        "subtitle": "Quand transférer tout de suite et quand congeler d'abord est plus malin.",
        "body": {
          "introduction": "Stratégie",
          "quickSummary": [
            {
              "title": "Le congelé égale ou dépasse maintenant le frais.",
              "description": "Les transferts congelés égalent ou dépassent désormais les taux de réussite des transferts frais dans la plupart des cas."
            },
            {
              "title": "Le « tout congeler » a ses indications claires.",
              "description": "La stratégie « tout congeler » est préférable en cas de PGT-A, de risque de SHO, ou quand les hormones sont élevées suite à la stimulation."
            },
            {
              "title": "Le frais a encore sa place.",
              "description": "Les transferts frais font gagner du temps et de l'argent quand les conditions sont bonnes. Il n'est pas toujours mieux de congeler."
            }
          ],
          "sections": [
            {
              "title": "Pourquoi le congelé a rattrapé son retard",
              "content": "La révolution de la vitrification a tout changé. Les anciennes méthodes de congélation lente abîmaient les cellules par la formation de cristaux de glace, donnant aux transferts frais un net avantage. La vitrification moderne (congélation flash à très basse température) atteint 95 % et plus de survie embryonnaire. Combinée aux preuves grandissantes que l'environnement utérin après stimulation ovarienne est suboptimal pour l'implantation, les transferts congelés sont devenus le standard dans beaucoup de cliniques.",
              "bulletPoints": [
                "Survie d'embryons congelés : plus de 95 % dans les cliniques expérimentées",
                "Survie d'ovocytes congelés : 90 à 97 % avec la vitrification",
                "Ancienne méthode de congélation lente : seulement 60 à 70 % de survie"
              ]
            },
            {
              "title": "Quand « tout congeler » a du sens",
              "content": "Il y a plusieurs situations où congeler tous les embryons et transférer lors d'un cycle ultérieur est clairement la meilleure stratégie.",
              "bulletPoints": [
                "Test PGT-A : les résultats prennent 1 à 2 semaines, les embryons doivent donc être congelés en attendant les résultats de la biopsie",
                "Progestérone élevée pendant la stimulation, ce qui peut nuire à la réceptivité endométriale",
                "Risque de syndrome d'hyperstimulation ovarienne (SHO) : la grossesse aggrave le SHO",
                "Souplesse d'agenda : transfert selon ton calendrier plutôt que celui du cycle",
                "Cycles en batch : accumuler des embryons de plusieurs ponctions avant de choisir le meilleur pour le transfert"
              ]
            },
            {
              "title": "Quand le transfert frais a du sens",
              "content": "Malgré la tendance au « tout congeler », les transferts frais restent une bonne option pour beaucoup de patientes. Si tu as répondu normalement à la stimulation, que tu ne fais pas de PGT, et que tu as un nombre limité d'embryons, un transfert frais évite le coût supplémentaire et le temps d'attente d'un cycle congelé. Certaines patientes préfèrent aussi l'élan émotionnel de transférer dans le même cycle plutôt que d'attendre un mois ou plus."
            },
            {
              "title": "Comparaison des données de réussite",
              "content": "De grandes études montrent que les transferts congelés et frais produisent des taux de grossesse comparables pour les répondeuses normales. Pour les forte répondeuses (beaucoup d'ovocytes recueillis) et les patientes avec PMOS (ex SOPK), les transferts congelés montrent un avantage statistiquement significatif. Pour les faibles répondeuses avec peu d'embryons, certaines données suggèrent que le frais pourrait être légèrement meilleur : moins tu as d'embryons, plus chaque cycle congélation-décongélation compte. Ton médecin doit adapter la recommandation à ta situation spécifique."
            },
            {
              "title": "Considérations de coût",
              "content": "En France, le transfert d'embryon congelé (TEC) est intégré au parcours AMP remboursé à 100 % par la Sécurité sociale, à condition que tu sois encore dans tes 4 tentatives de FIV (les TEC s'enchaînent à partir des embryons congelés d'un cycle de FIV et ne consomment pas de tentative supplémentaire). Hors parcours remboursé ou aux États-Unis, un TEC ajoute environ 3 000 à 6 000 $ / € : congélation, stockage, médicaments de préparation endométriale, suivi, et procédure de transfert."
            },
            {
              "title": "La tendance « tout congeler » : va-t-on trop loin ?",
              "content": "Certaines cliniques font maintenant du « tout congeler » par défaut pour presque toutes les patientes, quelles que soient les circonstances. Bien que cela simplifie la planification et puisse améliorer marginalement les résultats moyens, ce n'est pas toujours dans l'intérêt de la patiente individuelle. Une politique systématique de « tout congeler » peut ajouter coût et délai inutiles à des patientes qui s'en seraient aussi bien sorties avec un transfert frais. Demande à ta clinique pourquoi elle recommande le « tout congeler » dans ton cas précis, pas seulement par politique générale."
            },
            {
              "title": "Le facteur attente",
              "content": "Un point à considérer côté patiente : le TEC ajoute un cycle d'attente. Après la ponction, tu attendras généralement au moins un cycle menstruel complet avant le transfert. Pour certaines, ce délai est émotionnellement difficile : l'envie de transférer tout de suite peut être très forte. Pour d'autres, c'est une pause bienvenue après l'intensité de la stimulation, qui permet à ton corps et à ta tête de récupérer avant la fenêtre d'implantation. Aucune des deux réactions n'est plus « juste » que l'autre."
            }
          ],
          "references": [
            {
              "title": "Fresh versus elective frozen embryo transfer in IVF/ICSI cycles: a systematic review and meta-analysis",
              "authors": "Roque M, Haahr T, Geber S, Esteves SC, Humaidan P.",
              "journal": "Human Reproduction Update",
              "year": 2019,
              "url": "https://academic.oup.com/humupd/article/25/1/2/5089735",
              "evidenceLevel": "meta-analysis"
            },
            {
              "title": "Transfer of Fresh versus Frozen Embryos in Ovulatory Women",
              "authors": "Shi Y, Sun Y, Hao C, et al.",
              "journal": "New England Journal of Medicine",
              "year": 2018,
              "url": "https://www.nejm.org/doi/full/10.1056/NEJMoa1705334",
              "evidenceLevel": "rct"
            },
            {
              "title": "Fresh versus Frozen Embryos for Infertility in the Polycystic Ovary Syndrome",
              "authors": "Chen ZJ, Shi Y, Sun Y, et al.",
              "journal": "New England Journal of Medicine",
              "year": 2016,
              "url": "https://www.nejm.org/doi/full/10.1056/NEJMoa1513873",
              "evidenceLevel": "rct"
            },
            {
              "title": "Oocyte, embryo and blastocyst cryopreservation in ART: systematic review and meta-analysis comparing slow-freezing versus vitrification",
              "authors": "Rienzi L, Gracia C, Maggiulli R, et al.",
              "journal": "Human Reproduction Update",
              "year": 2017,
              "url": "https://academic.oup.com/humupd/article/23/2/139/2982525",
              "evidenceLevel": "meta-analysis"
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-after-failed-cycle",
      "locale": "fr",
      "variant": {
        "title": "Après un cycle qui n'a pas marché : quoi demander à ton médecin",
        "subtitle": "Un cycle qui n'a pas marché n'est pas la fin, c'est de l'information. Voici comment l'utiliser.",
        "body": {
          "introduction": "Étapes suivantes",
          "quickSummary": [
            {
              "title": "Un cycle qui n'a pas marché, c'est de la donnée.",
              "description": "Un cycle qui n'a pas marché apporte des informations qui peuvent améliorer la prochaine tentative."
            },
            {
              "title": "Demande ce qu'on changerait.",
              "description": "Pose des questions précises sur ce qu'on changerait : protocole, timing, examens supplémentaires."
            },
            {
              "title": "C'est OK de faire une pause.",
              "description": "C'est OK de prendre une pause, de chercher un second avis, ou de demander plus d'investigation."
            }
          ],
          "sections": [
            {
              "title": "D'abord, encaisser la déception",
              "content": "Avant de te jeter dans la suite, accorde-toi de l'espace pour ressentir la perte. Un cycle qui n'a pas marché est un vrai deuil : la perte d'espoir, de temps, d'argent, et d'efforts physiques. Il n'y a pas de bon timing pour digérer ça. Certaines sont prêtes à parler stratégie le lendemain ; d'autres ont besoin de semaines. Les deux réactions sont totalement valides. Ne laisse personne te précipiter dans le cycle suivant avant que tu sois émotionnellement prête."
            },
            {
              "title": "Questions à poser sur ce qui s'est passé",
              "content": "Quand tu te sens prête, prends rendez-vous pour un suivi avec ton médecin pour revoir le cycle en détail. Comprendre pourquoi ça n'a pas marché aide à orienter la suite.",
              "bulletPoints": [
                "Selon vous, pourquoi ce cycle n'a pas marché ?",
                "Est-ce probablement un problème de qualité embryonnaire ou un problème d'implantation ?",
                "Comment ma réponse à la stimulation s'est-elle comparée à ce que vous attendiez ?",
                "Y a-t-il eu des surprises pendant le cycle qui vous ont inquiété ?",
                "Si on a testé les embryons, qu'a montré la génétique ?"
              ]
            },
            {
              "title": "Changements de protocole à discuter",
              "content": "Un bon médecin suggérera proactivement des ajustements pour le prochain cycle à partir de ce qu'il a appris. S'il ne le fait pas, demande directement ce qu'il changerait.",
              "bulletPoints": [
                "Doses de médicaments différentes : plus élevées ou plus basses selon ta réponse",
                "Ajout d'hormone de croissance (HGH) pour potentiellement améliorer la qualité ovocytaire",
                "Changement de type de déclenchement : passer entre hCG et déclenchement Lupron",
                "Ajustement de la durée de stimulation ou ajout d'un protocole de préparation",
                "Changement d'approche pour le transfert : frais ou congelé, ou une préparation endométriale différente"
              ]
            },
            {
              "title": "Examens supplémentaires à envisager",
              "content": "Un cycle qui n'a pas marché révèle parfois le besoin d'une investigation plus poussée. Plusieurs examens peuvent mettre en lumière des problèmes invisibles avant le traitement. Important : certains tests autrefois populaires sont aujourd'hui remis en cause par les essais randomisés récents. C'est le cas notamment de l'ERA.",
              "bulletPoints": [
                "Hystéroscopie : visualisation directe de la cavité utérine pour repérer polypes, fibromes, ou adhérences",
                "Bilan de thrombophilie : recherche des troubles de la coagulation qui peuvent affecter l'implantation",
                "Fragmentation de l'ADN spermatique : le spermogramme standard ne mesure pas l'intégrité de l'ADN",
                "ERA (analyse de la réceptivité endométriale) : l'essai randomisé Doyle 2022 (JAMA) n'a pas montré d'amélioration du taux de naissance vivante chez les patientes tout-venant ; à réserver à des cas très spécifiques",
                "Bilan immunologique : preuves émergentes, encore controversé ; à discuter avec une équipe spécialisée"
              ]
            },
            {
              "title": "Quand réessayer vs quand investiguer plus",
              "content": "Après un premier cycle qui n'a pas marché avec une explication raisonnable (comme une qualité embryonnaire faible due au hasard), la plupart des médecins recommandent de réessayer avec des ajustements de protocole. Après deux cycles ou plus qui n'ont pas marché (surtout avec des embryons de bonne qualité), ça vaut le coup d'investiguer plus agressivement avant une nouvelle tentative. L'équilibre entre réessayer et tester plus dépend de ton âge, du nombre d'embryons restants, de ta situation financière, et de tes ressources émotionnelles."
            },
            {
              "title": "Envisager un second avis",
              "content": "Il n'y a aucune honte à chercher un second avis après un cycle qui n'a pas marché. C'est même l'une des raisons les plus courantes pour lesquelles les patientes consultent un autre médecin. Un regard neuf peut repérer quelque chose que ton médecin actuel a manqué, suggérer une autre approche, ou simplement confirmer que tu es sur la bonne voie. Tu peux obtenir un second avis sans changer entièrement de clinique."
            },
            {
              "title": "Le poids émotionnel de « réessayer »",
              "content": "Décider de faire un autre cycle, ce n'est pas qu'une décision médicale : c'est émotionnel, financier, et physique. C'est OK de sauter un cycle. C'est OK de fixer une limite au nombre de cycles que tu feras. C'est OK de changer complètement de direction. Ce qui compte, c'est que la décision te semble juste à toi, pas qu'elle suive un script prédéfini. Ta santé mentale compte autant que ton protocole."
            }
          ],
          "references": [
            {
              "title": "Effect of timing by endometrial receptivity testing vs standard timing of frozen embryo transfer on live birth in patients undergoing in vitro fertilization (RCT)",
              "authors": "Doyle N, Combs JC, Jahandideh S, et al.",
              "journal": "JAMA",
              "year": 2022,
              "url": "https://jamanetwork.com/journals/jama/fullarticle/2798845",
              "evidenceLevel": "rct"
            },
            {
              "title": "Clinical utility of sperm DNA fragmentation testing: practice recommendations based on clinical scenarios",
              "authors": "Agarwal A, Majzoub A, Esteves SC, Ko E, Ramasamy R, Zini A.",
              "journal": "Translational Andrology and Urology",
              "year": 2016,
              "url": "https://tau.amegroups.com/article/view/12463/13039",
              "evidenceLevel": "review"
            },
            {
              "title": "ESHRE guideline: recurrent pregnancy loss: an update in 2022",
              "authors": "Bender Atik R, Christiansen OB, Elson J, et al.",
              "journal": "Human Reproduction Open",
              "year": 2023,
              "url": "https://academic.oup.com/hropen/article/2023/1/hoad002/7045426",
              "evidenceLevel": "guideline"
            }
          ]
        }
      }
    }
  ]
}
